import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, Phone, MapPin } from "lucide-react";
import { Language } from "../types";
import { translations } from "../i18n/translations";

interface FooterProps {
  lang: Language;
}

export const Footer: React.FC<FooterProps> = ({ lang }) => {
  const t = translations[lang];
  const f = t.footer;

  return (
    <footer className="bg-slate-900 text-slate-300 pt-16 pb-8">
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              <span className="text-2xl font-bold text-white serif tracking-tight">
                Casas do Paço
              </span>
              <div className="text-[10px] uppercase tracking-[0.2em] font-medium text-white opacity-60">
                Alojamento Local
              </div>
            </Link>
            <p className="text-sm leading-relaxed max-w-xs">{f.description}</p>
            <div className="flex space-x-4">
              <a
                href="https://instagram.com/casasdopaco"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram — Casas do Paço"
                title="Instagram"
                className="hover:text-amber-500 transition-colors"
              >
                <Instagram size={20} aria-hidden="true" />
              </a>
              <a
                href="https://www.facebook.com/casasdopacoapartamentos/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook — Casas do Paço"
                title="Facebook"
                className="hover:text-amber-500 transition-colors"
              >
                <Facebook size={20} aria-hidden="true" />
              </a>
              <a
                href="mailto:info@casasdopaco.pt"
                aria-label={lang === "pt" ? "Enviar email" : "Send email"}
                title="Email"
                className="hover:text-amber-500 transition-colors"
              >
                <Mail size={20} aria-hidden="true" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-6 uppercase tracking-wider text-xs">
              {t.nav.units}
            </h4>
            <ul className="space-y-4 text-sm">
              <li>
                <Link
                  to="/unidades"
                  className="hover:text-white transition-colors"
                >
                  {lang === "pt"
                    ? "Ver todos os alojamentos"
                    : "View all properties"}
                </Link>
              </li>
              <li>
                <Link
                  to="/reservas"
                  className="hover:text-white transition-colors"
                >
                  {lang === "pt" ? "Pedir Reserva" : "Booking Request"}
                </Link>
              </li>
              <li>
                <Link
                  to="/gestao"
                  className="hover:text-white transition-colors"
                >
                  {lang === "pt"
                    ? "Gestão de alojamento local em Braga"
                    : "Short-term rental management in Braga"}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-6 uppercase tracking-wider text-xs">
              {t.nav.contact}
            </h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-amber-500 mt-0.5 shrink-0" />
                <span>
                  Rua do Raio, Nº 301 <br /> Edifício do Rechicho <br /> 5º
                  Andar, Sala 58
                  <br />
                  4710-923 Braga, Portugal
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-amber-500 shrink-0" />
                <span>+351 930 461 443</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-amber-500 shrink-0" />
                <span>info@casasdopaco.pt</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-6 uppercase tracking-wider text-xs">
              {f.legal}
            </h4>
            <ul className="space-y-4 text-sm">
              <li>
                <Link
                  to="/legal#termos"
                  className="hover:text-white transition-colors"
                >
                  {f.terms}
                </Link>
              </li>
              <li>
                <Link
                  to="/legal#privacidade"
                  className="hover:text-white transition-colors"
                >
                  {f.privacy}
                </Link>
              </li>
              <li>
                <Link
                  to="https://www.livroreclamacoes.pt/Inicio/"
                  className="hover:text-white transition-colors"
                >
                  {f.complaints}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center text-xs text-slate-500 gap-4">
          <p>
            © {new Date().getFullYear()} Casas do Paço. {t.common.allRights}
          </p>
          <div className="flex gap-4">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500"></span>{" "}
              {f.directBooking}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};
